import { Box } from '@chakra-ui/react';
import type React from 'react';
import { useRef, useState } from 'react';
import { ChevronDownIcon } from '@/assets/icons';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import usePermission from '@/hooks/usePermission';
import { useAuthStore } from '@/store';
import { MenuPrimitives as PMMenu } from '@/types/MenuPrimitives.types';
import { trackCoreClick } from '@/utils/analytics';
import type { ProfileMenuItem } from '@/utils/profileMenuItems';
import { profileMenuItems as externalMenuItems } from '@/utils/profileMenuItems';
import Show from '../Show';

interface ProfileMenuProps {
	trigger: React.ReactNode;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onNavigate?: (href?: string) => void;
	menuItems?: ProfileMenuItem[]; // Make menuItems optional prop
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
	trigger,
	isOpen,
	onOpenChange,
	onNavigate,
	menuItems: customMenuItems,
}) => {
	const [expandedItem, setExpandedItem] = useState<string | null>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const { hasPermission } = usePermission();

	// Get logout function from Zustand store
	const logout = useAuthStore((state) => state.logout);
	const user = useAuthStore((state) => state.user);

	// Theme colors
	const menuBg = useColorModeValue('white', 'gray.800');
	const textColor = useColorModeValue('gray.800', 'gray.100');

	// Use custom menu items if provided, otherwise use default profile menu items
	const menuItems: ProfileMenuItem[] = customMenuItems || externalMenuItems;

	const handleItemClick = async (item: ProfileMenuItem) => {
		if (item.submenu) {
			setExpandedItem(expandedItem === item.title ? null : item.title);
		} else {
			if (item.title === 'Log Out') {
				// Close menu immediately to prevent multiple clicks
				onOpenChange(false);
				setExpandedItem(null);
				trackCoreClick('logout_clicked', {
					source_module: 'profile_menu',
				});
				
				// Await logout to ensure it completes before navigation
				await logout();
				onNavigate?.(item.href);
			} else {
				onNavigate?.(item.href);
				onOpenChange(false);
				setExpandedItem(null);
			}
		}
	};

	const handleSubmenuItemClick = (item: ProfileMenuItem) => {
		onNavigate?.(item.href);
		onOpenChange(false);
		setExpandedItem(null);
	};

	return (
		<Box ref={triggerRef} cursor="pointer">
			<PMMenu.Root
				open={isOpen}
				onOpenChange={(d: { open: boolean }) => onOpenChange(d.open)}
			>
				<PMMenu.Trigger
					_focus={{ boxShadow: 'none', outline: 'none' }}
					_focusVisible={{ boxShadow: 'none', outline: 'none' }}
					border="none"
				>
					{trigger}
				</PMMenu.Trigger>
				<PMMenu.Positioner>
					<PMMenu.Content
						bg={menuBg}
						borderRadius="xl"
						boxShadow="xl"
						py="2.5"
						width="55"
						minHeight="auto"
						maxHeight="100"
						zIndex="popover"
						color={textColor}
						display="flex"
						flexDirection="column"
						gap="1.5"
						transition="height 0.3s ease-in-out"
						overflow="hidden"
					>
						{menuItems.map((item) => (
							<Show
								when={
									(!item.permissions ||
									!item?.permissions?.length ||
									hasPermission(item.permissions))
									&&
									// Exclude 'Denial Support' if user has 'woundcare+' port available
									(item.title !== 'Denial Support' || (item.title === 'Denial Support' &&
										(!user?.userInformation?.portsAvailable?.toLowerCase().includes('woundcare+'))
									))
								}
								key={item.title}
							>
								<Box key={item.title}>
									<Box
										px="3"
										py="2"
										display="flex"
										alignItems="center"
										justifyContent="space-between"
										cursor="pointer"
										_hover={{
											bg: 'brand.solidGradient',
											color: 'white',
											'& > div > div': { color: 'white' },
											'& svg': { color: 'white' },
										}}
										onClick={() => handleItemClick(item)}
										transition="all 0.2s"
										borderRadius="md"
									>
										<Box display="flex" alignItems="center" gap="3">
											<Box display="flex" alignItems="center" color={textColor}>
												{item.icon}
											</Box>
											<Box fontSize="sm" fontWeight="500" color={textColor}>
												{item.title}
											</Box>
										</Box>

										<Show when={!!item.submenu}>
											<Box
												display="flex"
												alignItems="center"
												transform={
													expandedItem === item.title
														? 'rotate(180deg)'
														: 'rotate(0deg)'
												}
												transition="transform 0.3s ease-in-out"
											>
												<ChevronDownIcon width={12} height={12} />
											</Box>
										</Show>
									</Box>

									{/* Submenu */}
									<Show when={!!item.submenu}>
										<Box
											overflow="hidden"
											transition="all 0.3s ease-in-out"
											maxHeight={expandedItem === item.title ? '48' : '0'}
											opacity={expandedItem === item.title ? 1 : 0}
										>
											{item.submenu?.map((subItem) => (
												<Box
													key={subItem.title}
													px="3"
													py="1.5"
													display="flex"
													alignItems="center"
													gap="3"
													cursor="pointer"
													_hover={{
														bg: 'brand.solidGradient',
														color: 'white',
														'& > div': { color: 'white' },
														'& svg': { color: 'white' },
													}}
													onClick={() => handleSubmenuItemClick(subItem)}
													transition="all 0.2s"
													borderRadius="md"
													ml="2"
												>
													<Box
														display="flex"
														alignItems="center"
														color={textColor}
													>
														{subItem.icon}
													</Box>
													<Box fontSize="sm" fontWeight="400" color={textColor}>
														{subItem.title}
													</Box>
												</Box>
											))}
										</Box>
									</Show>
								</Box>
							</Show>
						))}
					</PMMenu.Content>
				</PMMenu.Positioner>
			</PMMenu.Root>
		</Box>
	);
};

export default ProfileMenu;
